/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  Axios,
  AxiosRequestConfig,
  AxiosResponse,
  ResponseType,
} from "axios";
import * as Sentry from "@sentry/react";

const API_URL = import.meta.env.VITE_API_URL;

interface Response<T> {
  data: T;
  message: string;
  error: string;
}
export class AuthenticatedHttpClient {
  // private axios: AxiosInstance;
  http: Axios;
  constructor(tokenCallBack: (template: string) => Promise<string | null>) {
    this.http = axios.create({
      baseURL: API_URL as string,
    });
    this.http.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const requestInfo = {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
        };

        // Only log to Sentry if it's not a network error (backend not available)
        if (error.code !== "ERR_NETWORK" && error.message !== "Network Error") {
          Sentry.withScope((scope) => {
            scope.setExtra("request", requestInfo);

            if (error.response) {
              // Add response data to error context if available
              scope.setExtra("response", {
                status: error.response.status,
                data: error.response.data,
              });
            }

            // Capture the actual error
            Sentry.captureException(error);
          });
        }

        // For network errors, return a more graceful error
        if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
          console.warn(
            "Network error - backend may not be available:",
            requestInfo.url,
          );
          return Promise.reject(new Error("Backend service unavailable"));
        }

        await Promise.reject(error);
        return error.response?.data || { error: error.message };
      },
    );

    this.http.interceptors.request.use(
      async (config) => {
        const token = await tokenCallBack("backend");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        Sentry.captureException(error);
        Promise.reject(error);
      },
    );
  }

  // public async get<T>(url: string, config?: AxiosRequestConfig): Promise<any> {
  //   const response = await this.http.get<T>(url, config);
  //   return response;
  // }

  async get<T>(
    url: string,
    params?: any,
    responseType?: ResponseType,
  ): Promise<Response<T>> {
    return this.http.get(url, {
      params,
      responseType: responseType,
    });
  }

  public async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    const response = await this.http.post<T>(url, data, config);
    return response;
  }

  public async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    const response = await this.http.put<T>(url, data, config);
    return response;
  }

  public async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    const response = await this.http.patch<T>(url, data, config);
    return response;
  }

  public async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    const response = await this.http.delete<T>(url, config);
    return response;
  }
}
